import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UploadSimple, Bank, ArrowsClockwise } from "@phosphor-icons/react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function ImportCsv({ file }) {
  const [csvFile, setCsvFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const pwKey = `/powens/files/${file.id}/status`;
  const { data: pw } = useSWR(pwKey, fetcher);
  const [pwBusy, setPwBusy] = React.useState(false);

  const connectPowens = async () => {
    setPwBusy(true);
    try {
      const r = await api.post(`/powens/files/${file.id}/connect`);
      window.location.assign(r.data.redirect_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion Powens impossible");
      setPwBusy(false);
    }
  };

  const syncPowens = async () => {
    setPwBusy(true);
    try {
      const r = await api.post(`/powens/files/${file.id}/sync`);
      toast.success(`${r.data.created} écriture(s) importée(s) (${r.data.fetched} transaction(s) reçue(s))`);
      mutate(pwKey);
      mutate(`/accounting/files/${file.id}/entries`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Synchronisation impossible"); }
    finally { setPwBusy(false); }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!csvFile) { toast.error("Sélectionnez un fichier CSV"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await api.post(`/accounting/files/${file.id}/import-bank`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.created} écriture(s) importée(s) dans le journal Banque`);
      mutate(`/accounting/files/${file.id}/entries`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import impossible"); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {pw?.configured && (
        <div className="border border-[#C7BFE8] bg-[#F7F5FC] rounded-md p-6" data-testid="powens-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#2E3192] mb-2 font-semibold">Connexion bancaire automatique (Powens)</div>
          {pw.connected ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-zinc-700">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 mr-2" data-testid="powens-status">Banque connectée</span>
                {pw.last_sync ? `Dernière synchro : ${new Date(pw.last_sync).toLocaleString("fr-FR")} · ${pw.imported_total} transaction(s) importée(s)` : "Aucune synchronisation effectuée"}
              </div>
              <Button onClick={syncPowens} disabled={pwBusy} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="powens-sync-btn">
                <ArrowsClockwise size={16} className="mr-1" /> {pwBusy ? "Synchronisation…" : "Synchroniser"}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-zinc-600 max-w-md">
                Connectez le compte bancaire du client via l'interface sécurisée Powens (SCA / DSP2). Les transactions seront importées en brouillards comme pour le CSV.
              </p>
              <Button onClick={connectPowens} disabled={pwBusy} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="powens-connect-btn">
                <Bank size={16} className="mr-1" /> {pwBusy ? "Ouverture…" : "Connecter la banque"}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="border border-zinc-200 bg-white rounded-md p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Import d'un relevé bancaire (CSV)</div>
        <p className="text-sm text-zinc-600 mb-4">
          Colonnes attendues : <span className="font-mono text-xs">date, libellé, montant</span> (ou <span className="font-mono text-xs">débit / crédit</span>).
          Chaque ligne crée une écriture <span className="font-medium">Banque ↔ Compte d'attente (4711/4712)</span> en <span className="font-medium">brouillard</span> dans le journal BQ,
          à vérifier, imputer sur les comptes définitifs puis valider depuis l'onglet Saisie.
        </p>
        <form onSubmit={upload} className="space-y-4">
          <div>
            <Label>Fichier CSV</Label>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="import-file" />
          </div>
          <Button type="submit" disabled={uploading} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="import-submit">
            <UploadSimple size={16} className="mr-1" /> {uploading ? "Import en cours…" : "Importer"}
          </Button>
        </form>
      </div>

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 text-sm" data-testid="import-result">
          <div className="font-medium text-emerald-900">Import terminé</div>
          <div className="text-emerald-800 mt-1">{result.created} écriture(s) créée(s) · {result.skipped} ligne(s) ignorée(s) (date illisible ou hors exercice ouvert).</div>
        </div>
      )}
    </div>
  );
}
